import React from 'react';
import { definePage, defineResource } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import EmployeeRuleForm from 'src/components/Employee/EmployeeRuleForm';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  tenantEmployeeRuleCreate,
  tenantEmployeeRuleDelete,
  tenantEmployeeRuleUpdate,
  tenantEmployeeRules,
} from 'src/services/apis/tenantRequests';
import { QUERIES } from 'src/utils/react-query';
import { splitTags } from 'src/utils/tags';

const employeeRules = defineResource({
  key: QUERIES.EMPLOYEE_RULES,
  // The hand-written hook sent no limit, so the server (alwaysPaginate, no configured take)
  // returned every row in one shot; the crud layer's paginated query needs a concrete page
  // size, and 1000 is the practical match — the same reach the pilot used for an
  // effectively-unbounded list.
  pageSize: 1000,
  api: {
    list: tenantEmployeeRules,
    create: tenantEmployeeRuleCreate,
    update: tenantEmployeeRuleUpdate,
    remove: tenantEmployeeRuleDelete,
  },
  // The hand-written page filtered title/description in memory over the fetched set.
  search: 'client',
  searchFields: ['title', 'description'],
  // The stored VND amount (the column renders it as K), compared on the server.
  filterable: [{ field: 'fine', labelKey: 'common.fine', type: 'number' }],
});

export const employeeRulesPage = definePage(employeeRules, {
  titleKey: 'titles.employeeRulesPage',
  descriptionKey: 'common.employeeRules',
  createLabelKey: 'actions.createNewEmployeeRule',
  updateLabelKey: 'actions.updateEmployeeRule',
  deleteHeadingKey: 'messages.areYouSureToDeleteThisEmployeeRule',
  columns: [
    // The column a user edits by title must survive every prefs reconciliation.
    { key: 'title', headerKey: 'common.title', sortable: true, alwaysVisible: true },
    { key: 'description', headerKey: 'common.description' },
    // currency renders via the portal's useMoney formatter — the same one the hand-written
    // cell used, minus the empty-string gap it left whenever fine was missing.
    { key: 'fine', headerKey: 'common.fine', display: { kind: 'currency' } },
  ],
  form: EmployeeRuleForm,
  emptyState: <EmptyState />,
  toPayload: (values: any, params) => ({
    ...values,
    restaurantId: params.restaurantId,
    tags: splitTags(values.tags),
    fine: Number(values.fine || 0),
  }),
  useTitleVars: () => ({ restaurantName: useCurrentRestaurant()?.name }),
});
